import { SecretStorage } from "./secrets.js";
import {
  ApiConfig,
  AppConfig,
  Config,
  CredentialsConfig,
  IdentityConfig,
  StoredConfig,
} from "./schemas.js";
import { ConfigStore } from "./store.js";

/** Responsible for providing the runtime configuration for the CLI that is comprised of
 * any locally cached data that is needed in order to interact with the user's resources.
 *
 * It is responsible for managing the lifecycle of the user's cached config data including items such as:
 * - API Keys
 * - Ephemeral JWTs
 * - User's profile data
 */
export class ConfigProvider {
  private configStore: ConfigStore;

  constructor(
    private readonly appConfig: AppConfig,
    private readonly secretStore: SecretStorage<StoredConfig>,
  ) {
    this.configStore = new ConfigStore(secretStore, appConfig);
  }

  /** Call this method to initialize the config provider. */
  async init(): Promise<void> {
    await this.configStore.init();
  }

  async reset() {
    await this.configStore.clear();
    await this.configStore.init();
  }

  /**
   * Returns a read-only reference to the current config. The caller should only use
   * explicit setters to update this.
   */
  async getConfig(): Promise<Readonly<Config>> {
    // TODO: Merge in overrides
    return await this.configStore.load();
  }

  async update(config: Partial<Config>) {
    await this.configStore.update(config);
  }

  async updateApiUrl(apiUrl: string) {
    await this.configStore.update({ api: { url: apiUrl } });
  }

  async updateIdentity(identity: IdentityConfig) {
    await this.configStore.update({ identity });
  }

  async updateCredentials(credentials: CredentialsConfig) {
    await this.configStore.update({ credentials });
  }
}
